import { useState } from 'react';
import { Box, Button, CircularProgress, Container, MobileStepper, Stack } from '@mui/material';
import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';

import { useIsUnitAdmin } from '@/hooks/use-is-unit-admin';
import { useOnboardingPublish } from '@/hooks/use-onboarding-publish';
import { useUnitDetails, type UnitDetailsIdentity } from '@/hooks/use-unit-details';
import type { OrgProfile, SiteGoal } from '@/components/org-settings/types';
import type { PrivacyTier } from '@/lib/onboarding/privacy-tiers';
import { SITE_THEME_PRESETS, type SiteThemePreset } from '@/lib/site-styles/theme-presets';
import type { SiteTemplate } from '@/lib/templates/types';
import type { Site } from '@/types/database';
import { ConfirmIdentityStep } from './ConfirmIdentityStep';
import { GoalStep } from './GoalStep';
import { TemplateStep } from './TemplateStep';
import { BrandStep } from './BrandStep';
import { PrivacyStep } from './PrivacyStep';
import { ReviewPublishStep } from './ReviewPublishStep';
import { LaunchChecklist } from './LaunchChecklist';

const STEPS = ['identity', 'goal', 'template', 'brand', 'privacy', 'review'] as const;
type Step = (typeof STEPS)[number];

interface OnboardingWizardProps {
  site: Site;
  profile: OrgProfile;
  unitName: string;
  onComplete: () => void;
}

// The setup wizard (spec section 4) — shown in place of the bare template
// gallery the first time a unit has zero pages. Ten-minutes-to-published
// is the target; every step here has a sensible default so a webmaster in
// a hurry can click straight through to Publish.
//
// The identity step is seeded from the shared unit_details record, so
// anything already entered in the hub's Details page shows up pre-filled
// here rather than being re-asked. That record loads async, so this shell
// waits for it before mounting the inner wizard whose useState seeds from
// it once (a later arriving value wouldn't re-seed already-created state).
export function OnboardingWizard({ site, profile, unitName, onComplete }: OnboardingWizardProps) {
  const { unitDetails, loading } = useUnitDetails();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={24} aria-label="Loading unit details" />
      </Box>
    );
  }

  return (
    <OnboardingWizardContent
      site={site}
      profile={profile}
      unitName={unitName}
      unitDetails={unitDetails}
      onComplete={onComplete}
    />
  );
}

function OnboardingWizardContent({
  site,
  profile,
  unitName,
  unitDetails,
  onComplete,
}: OnboardingWizardProps & { unitDetails: UnitDetailsIdentity | null }) {
  const { isAdmin } = useIsUnitAdmin();
  const [stepIndex, setStepIndex] = useState(0);
  const [finished, setFinished] = useState(false);

  const [unitIdentifier, setUnitIdentifier] = useState(unitDetails?.unit_number ?? '');
  const [councilName, setCouncilName] = useState(unitDetails?.council_name ?? '');
  const [districtName, setDistrictName] = useState(unitDetails?.district_name ?? '');
  const [charteredOrgName, setCharteredOrgName] = useState(unitDetails?.chartered_org_name ?? '');
  const [goal, setGoal] = useState<SiteGoal>('both');
  const [template, setTemplate] = useState<SiteTemplate | null>(null);
  const [themePreset, setThemePreset] = useState<SiteThemePreset>(SITE_THEME_PRESETS[2]);
  const [privacyTier, setPrivacyTier] = useState<PrivacyTier>('open');

  const { publish, publishing, error } = useOnboardingPublish(site, unitName);

  const step: Step = STEPS[stepIndex];
  const canGoNext = step !== 'template' || Boolean(template);

  const handlePublish = async () => {
    if (!template) return;
    const succeeded = await publish({
      template,
      unitIdentifier,
      councilName,
      districtName,
      charteredOrgName,
      goal,
      themePreset,
      privacyTier,
    });
    if (succeeded) setFinished(true);
  };

  if (finished) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <LaunchChecklist site={site} onDone={onComplete} />
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Stack spacing={4}>
        <Box>
          {step === 'identity' ? (
            <ConfirmIdentityStep
              unitName={unitName}
              unitType={profile.unit_type}
              unitIdentifier={unitIdentifier}
              onChangeUnitIdentifier={setUnitIdentifier}
              councilName={councilName}
              onChangeCouncilName={setCouncilName}
              districtName={districtName}
              onChangeDistrictName={setDistrictName}
              charteredOrgName={charteredOrgName}
              onChangeCharteredOrgName={setCharteredOrgName}
              editable={isAdmin}
            />
          ) : step === 'goal' ? (
            <GoalStep goal={goal} onChangeGoal={setGoal} />
          ) : step === 'template' ? (
            <TemplateStep
              unitType={profile.unit_type}
              unitName={unitName}
              unitIdentifier={unitIdentifier}
              onSelectTemplate={(selected) => {
                setTemplate(selected);
                setStepIndex((index) => index + 1);
              }}
            />
          ) : step === 'brand' ? (
            <BrandStep themePreset={themePreset} onChangeThemePreset={setThemePreset} />
          ) : step === 'privacy' ? (
            <PrivacyStep tier={privacyTier} onChangeTier={setPrivacyTier} />
          ) : (
            <ReviewPublishStep
              unitName={unitName}
              template={template}
              themePreset={themePreset}
              privacyTier={privacyTier}
              publishing={publishing}
              error={error}
              onPublish={() => void handlePublish()}
            />
          )}
        </Box>

        <MobileStepper
          variant="dots"
          steps={STEPS.length}
          position="static"
          activeStep={stepIndex}
          sx={{ bgcolor: 'transparent', px: 0 }}
          backButton={
            <Button size="small" disabled={stepIndex === 0} onClick={() => setStepIndex((index) => index - 1)} startIcon={<CaretLeftIcon size={16} />}>
              Back
            </Button>
          }
          nextButton={
            step === 'review' ? (
              <Box sx={{ width: 76 }} />
            ) : (
              <Button
                size="small"
                disabled={!canGoNext}
                onClick={() => setStepIndex((index) => index + 1)}
                endIcon={<CaretRightIcon size={16} />}
              >
                Next
              </Button>
            )
          }
        />
      </Stack>
    </Container>
  );
}
